/**
 * files/processors.js
 * File processing and content extraction
 * Dynamically loads processing libraries as needed
 */

import { formatFileSize } from '../utils.js?v=302';

// Library loading state
let pdfjsLoaded = false;
let mammothLoaded = false;
let xlsxLoaded = false;

/**
 * Read a file as base64 data
 * @param {File} file - File object to read
 * @returns {Promise<string>} Base64 encoded string (without data URL prefix)
 */
async function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // Remove the data URL prefix (e.g., "data:image/png;base64,")
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Failed to read file as base64'));
    reader.readAsDataURL(file);
  });
}

/**
 * File types supported by OpenClaw's server-side input_file processing.
 * These types are sent as base64 input_file items instead of being parsed client-side.
 * Server-side parsing is superior (e.g., PDF rasterization fallback for image-sparse PDFs).
 */
const SERVER_PARSED_TYPES = new Set(['pdf', 'txt', 'md', 'html', 'csv', 'json']);

/**
 * Get MIME type from file extension
 * @param {string} ext - File extension
 * @returns {string} MIME type
 */
function getMimeType(ext) {
  const mimeTypes = {
    'jpg': 'image/jpeg',
    'jpeg': 'image/jpeg',
    'png': 'image/png',
    'gif': 'image/gif',
    'webp': 'image/webp'
  };
  return mimeTypes[ext.toLowerCase()] || 'application/octet-stream';
}

/**
 * Dynamically load PDF.js library
 * @returns {Promise<void>}
 */
async function loadPdfJs() {
  if (pdfjsLoaded) return;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js?v=302';
    script.onload = () => {
      // Configure PDF.js worker
      if (window.pdfjsLib) {
        window.pdfjsLib.GlobalWorkerOptions.workerSrc =
          'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js?v=302';
        pdfjsLoaded = true;
        resolve();
      } else {
        reject(new Error('PDF.js failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load PDF.js?v=302'));
    document.head.appendChild(script);
  });
}

/**
 * Dynamically load Mammoth.js library for Word documents
 * @returns {Promise<void>}
 */
async function loadMammoth() {
  if (mammothLoaded) return;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mammoth@1.6.0/mammoth.browser.min.js?v=302';
    script.onload = () => {
      if (window.mammoth) {
        mammothLoaded = true;
        resolve();
      } else {
        reject(new Error('Mammoth.js failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Mammoth.js?v=302'));
    document.head.appendChild(script);
  });
}

/**
 * Dynamically load SheetJS (xlsx) library for Excel files
 * @returns {Promise<void>}
 */
async function loadXlsx() {
  if (xlsxLoaded) return;

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/xlsx@0.18.5/dist/xlsx.full.min.js?v=302';
    script.onload = () => {
      if (window.XLSX) {
        xlsxLoaded = true;
        resolve();
      } else {
        reject(new Error('SheetJS failed to load'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load SheetJS'));
    document.head.appendChild(script);
  });
}

/**
 * Process a file and extract its content
 * Determines file type and uses appropriate extraction method
 * @param {File} file - File object from input
 * @returns {Promise<Object>} File data object with name, type, content, size
 */
export async function processFile(file) {
  const ext = file.name.split(".").pop().toLowerCase();
  const size = file.size;

  // Server-parsed files: send raw base64 to OpenClaw for server-side processing
  // Server handles PDF (with rasterization fallback), txt, md, html, csv, json
  if (SERVER_PARSED_TYPES.has(ext) && size <= 5 * 1024 * 1024) {
    const base64Data = await readFileAsBase64(file);
    // Also extract text client-side for preview in the chat UI
    let previewText = `[${ext.toUpperCase()}: ${formatFileSize(size)}]`;
    try {
      if (ext === 'pdf') {
        await loadPdfJs();
        const arrayBuffer = await file.arrayBuffer();
        previewText = await extractTextFromPDF(arrayBuffer);
        previewText = previewText.substring(0, 200) + (previewText.length > 200 ? '...' : '');
      } else {
        const text = await extractTextFromFile(file);
        previewText = text.substring(0, 200) + (text.length > 200 ? '...' : '');
      }
    } catch (e) {
      // Preview extraction failed, use placeholder
    }
    return {
      name: file.name,
      type: ext,
      content: previewText,
      size: size,
      rawFile: true,
      base64Data: base64Data,
    };
  }
  // PDF too large for server parsing, fall back to client-side
  if (ext === "pdf") {
    await loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const text = await extractTextFromPDF(arrayBuffer);
    return { name: file.name, type: ext, content: text, size: size };
  }
  // Office files (not supported by server input_file, parse client-side)
  else if (["docx", "xls", "xlsx"].includes(ext)) {
    const arrayBuffer = await file.arrayBuffer();
    const text = await extractTextFromOffice(arrayBuffer, ext);
    return { name: file.name, type: ext, content: text, size: size };
  }
  // Plain text and data files not handled by server
  else if (["xml", "css", "js", "py", "java", "yaml", "yml", "toml", "ini", "env"].includes(ext)) {
    const text = await extractTextFromFile(file);
    return { name: file.name, type: ext, content: text, size: size };
  }
  // Code files
  else if (["c", "cpp", "ts", "go", "rs", "rb", "php", "swift", "kt", "r", "sql", "sh", "bash", "zsh", "ps1", "bat"].includes(ext)) {
    const text = await extractTextFromFile(file);
    return { name: file.name, type: ext, content: text, size: size };
  }
  // Image files - encode as base64 for vision support
  else if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    const base64Data = await readFileAsBase64(file);
    const mimeType = getMimeType(ext);
    return { 
      name: file.name, 
      type: ext, 
      content: `[Image: ${formatFileSize(size)}]`, 
      size: size,
      isImage: true,
      base64: base64Data,
      mimeType: mimeType,
      dataUrl: `data:${mimeType};base64,${base64Data}`
    };
  }
  // Other image files (metadata only - not supported for vision)
  else if (["svg", "ico", "bmp"].includes(ext)) {
    return { name: file.name, type: ext, content: `[Image: ${formatFileSize(size)}]`, size: size };
  }
  // Archives (metadata only)
  else if (["zip", "rar", "7z", "tar", "gz", "bz2", "xz"].includes(ext)) {
    return { name: file.name, type: ext, content: `[Archive: ${formatFileSize(size)}]`, size: size };
  }
  // PowerPoint
  else if (ext === "pptx") {
    return { name: file.name, type: ext, content: `[PowerPoint: ${formatFileSize(size)}]`, size: size };
  }
  // Default - return filename only
  else {
    return { name: file.name, type: ext, content: file.name, size: size };
  }
}

/**
 * Extract text content from PDF file
 * @param {ArrayBuffer} arrayBuffer - PDF file data
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromPDF(arrayBuffer) {
  try {
    const pdf = await window.pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    let text = "";

    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      text += content.items.map((item) => item.str).join(" ") + "\n\n";
    }

    return text || "[PDF content could not be extracted]";
  } catch (error) {
    console.error("PDF extraction error:", error);
    return "[Error extracting PDF content]";
  }
}

/**
 * Extract text content from Office files (Word, Excel)
 * @param {ArrayBuffer} arrayBuffer - Office file data
 * @param {string} type - File type ('docx', 'xls', 'xlsx')
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromOffice(arrayBuffer, type) {
  try {
    if (type === "docx") {
      await loadMammoth();
      const result = await window.mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } else if (["xls", "xlsx"].includes(type)) {
      await loadXlsx();
      const workbook = window.XLSX.read(arrayBuffer, { type: "array" });
      let text = "";

      workbook.SheetNames.forEach((sheetName) => {
        const sheet = workbook.Sheets[sheetName];
        text += `Sheet: ${sheetName}\n`;
        text += window.XLSX.utils.sheet_to_csv(sheet) + "\n\n";
      });

      return text;
    }
  } catch (error) {
    console.error("Office extraction error:", error);
    return "[Error extracting file content]";
  }
  return "";
}

/**
 * Extract text from plain text files
 * Handles JSON pretty-printing and large file truncation
 * @param {File} file - File object
 * @returns {Promise<string>} Extracted text content
 */
export async function extractTextFromFile(file) {
  try {
    // For small files, use FileReader text
    if (file.size < 1024 * 1024) { // 1MB limit for text extraction
      const text = await file.text();

      // If JSON, pretty-print for better readability
      const ext = file.name.split(".").pop().toLowerCase();
      if (ext === "json") {
        try {
          const parsed = JSON.parse(text);
          return JSON.stringify(parsed, null, 2);
        } catch (e) {
          return text; // Not valid JSON, return as-is
        }
      }

      return text;
    } else {
      // For large files, truncate with warning
      const chunk = file.slice(0, 50000);
      const text = await chunk.text();
      return text + "\n\n[File truncated - content exceeds 50KB]";
    }
  } catch (error) {
    console.error("Text extraction error:", error);
    return "[Error reading file content]";
  }
}
